/**
 * FamilyRatings Component
 *
 * Displays family member ratings for a recipe with:
 * - Avatar initials for each family member
 * - Star rating display
 * - Optional notes
 * - "Add rating" button to open AddRatingModal
 * - Edit button on each rating to open EditRatingModal
 */

import { useState, useCallback } from 'react';
import type { RecipeRating, FamilyMember } from '@/types/food';
import { RatingStars } from './RatingStars';
import { AddRatingModal } from './AddRatingModal';
import { EditRatingModal } from './EditRatingModal';

interface FamilyRatingsProps {
  ratings: RecipeRating[];
  familyMembers: FamilyMember[];
  /** Recipe ID for API calls */
  recipeId: number;
  /** Callback when ratings change (for page refresh) */
  onRatingsChange?: () => void;
}

// Avatar gradient colors for variety
const AVATAR_GRADIENTS = [
  'from-indigo-500 to-purple-600',
  'from-pink-500 to-rose-500',
  'from-cyan-400 to-blue-500',
  'from-orange-400 to-amber-500',
  'from-emerald-400 to-teal-500',
  'from-violet-500 to-fuchsia-500',
];

function getAvatarGradient(index: number): string {
  return AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

function PencilIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function PlusIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

export function FamilyRatings({
  ratings,
  familyMembers,
  recipeId,
  onRatingsChange,
}: FamilyRatingsProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRating, setEditingRating] = useState<RecipeRating | null>(null);

  // Find members who haven't rated yet
  const ratedMemberIds = ratings.map((r) => r.familyMemberId);
  const canAddRating = familyMembers.some((m) => !ratedMemberIds.includes(m.id));

  const handleAddSave = useCallback(() => {
    onRatingsChange?.();
  }, [onRatingsChange]);

  const handleEditSave = useCallback(() => {
    onRatingsChange?.();
  }, [onRatingsChange]);

  const handleDelete = useCallback(() => {
    onRatingsChange?.();
  }, [onRatingsChange]);

  return (
    <div>
      <div className="flex flex-col gap-3">
        {ratings.map((rating, index) => (
          <div
            key={rating.id}
            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg group"
          >
            <div
              className={`w-10 h-10 rounded-full bg-gradient-to-br ${getAvatarGradient(index)} flex items-center justify-center text-white font-semibold text-base flex-shrink-0`}
            >
              {getInitials(rating.familyMemberName)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900">
                {rating.familyMemberName}
              </div>
              <div className="mt-0.5">
                <RatingStars rating={rating.rating} showValue={false} size="sm" />
              </div>
              {rating.notes && (
                <div className="text-sm text-gray-500 mt-1 truncate">
                  {rating.notes}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setEditingRating(rating)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={`Edit ${rating.familyMemberName}'s rating`}
              data-testid={`edit-rating-${rating.id}`}
            >
              <PencilIcon />
            </button>
          </div>
        ))}

        {ratings.length === 0 && (
          <p className="text-gray-500 text-sm py-2">No ratings yet.</p>
        )}

        {canAddRating && (
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 p-3 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-600 transition-colors"
            data-testid="add-rating-button"
          >
            <PlusIcon />
            Add rating
          </button>
        )}
      </div>

      {/* Add Rating Modal */}
      <AddRatingModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddSave}
        recipeId={recipeId}
        familyMembers={familyMembers}
        existingMemberIds={ratedMemberIds}
      />

      {/* Edit Rating Modal */}
      <EditRatingModal
        isOpen={editingRating !== null}
        onClose={() => setEditingRating(null)}
        onSave={handleEditSave}
        onDelete={handleDelete}
        recipeId={recipeId}
        rating={editingRating}
      />
    </div>
  );
}
